import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Staff from "@/components/Staff";
import { DriftingLetters, FloatingNotes } from "@/components/BackgroundFx";
import SmokeCanvas from "@/components/SmokeCanvas";
import { SlideRenderer } from "@/components/SlideRenderer";
import { GhostLetter } from "@/components/GhostLetter";
import { ProgressDots } from "@/components/SlideShell";
import NoIndex from "@/components/NoIndex";
import { buildLetterSequence } from "@/lib/composition";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import useLiveStream from "@/lib/useLiveStream";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time between each letter drop (ms). Tuned to feel unhurried on a
// projector — Dan is playing in real time, this is a visual accompaniment,
// not a metronome.
const STEP_MS = 480;

// Fantasia-mode ghost letter tuning (Hebrew only for now).
// Duration is computed *per selection* based on the letter count so a
// short phrase gets long dramatic ghosts while a dense phrase fades them
// out fast enough to keep the page from getting cluttered. See
// `computeGhostDuration` below.
const GHOST_MIN_MS = 3200;
const GHOST_MAX_MS = 7200;
// Painterly palette that reads on our deep-navy backdrop. Cream/white
// hues are deliberately excluded so ghosts don't disappear when they
// drift toward the light-colored staff card at the bottom of the page.
const GHOST_PALETTE = [
  "#F2D589", // gold
  "#FCA07A", // coral
  "#E86A92", // rose
  "#B79CE8", // lavender
  "#A774FF", // seafoam
  "#FFB86B", // amber
  "#7BC4F2", // sky
  "#FF7EB6", // pink
];

// The WebGL fluid sim internally amplifies each splat color by 10×
// before adding it to the dye field. Full-intensity hex → one splat
// blows out to near-white; 2-3 overlaps sum to pure white regardless
// of hue. We dim the palette to ~12% for splat colors so each splat
// contributes ~1.2 per channel: bright enough to read on its own,
// dim enough that 3-4 concurrent splats stay in-hue instead of
// summing to white. (5% was too dim to see, 18% blew out to white.)
const SMOKE_PALETTE = GHOST_PALETTE.map((hex) => {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * 0.12);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * 0.12);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * 0.12);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
});

function computeGhostDuration(letterCount) {
  // Aim for roughly 6-8 concurrent ghosts on screen. STEP_MS between drops
  // means duration ≈ target concurrent × step; we cap and floor so 3-letter
  // phrases don't feel too slow and 20-letter phrases don't feel busy.
  const raw = 8500 - letterCount * 350;
  return Math.max(GHOST_MIN_MS, Math.min(GHOST_MAX_MS, raw));
}

/**
 * Compute a Fantasia ghost spawn point along the TOP EDGE of the staff
 * card. Ghosts then float upward toward the headline and fade as they
 * rise — pairs visually with the colored smoke that falls below the
 * staff, so the two effects sandwich the notation.
 *
 * X spawn is spread across most of the viewport width (with a slight
 * center bias) so ghosts don't cluster at one anchor point.
 */
function pickStaffTopSpawn(staffRect, viewportW) {
  const t = Math.random();
  const biased = 0.5 + (t - 0.5) * 0.85;
  const cx = biased * viewportW;
  // 6px gap above the staff card's top edge so the ghost's bottom
  // edge sits just above the top of the notation.
  const cy = staffRect.top - 6;
  return { cx, cy };
}

/**
 * Drift UPWARD only. Vertical magnitude carries the ghost roughly 30-45vh
 * upward toward the headline area, where opacity has faded to zero by the
 * time it would collide with the huge Hebrew title. Small horizontal
 * drift keeps things painterly without letting ghosts shoot off-screen.
 */
function pickDriftUp() {
  const dy = -(30 + Math.random() * 15);              // -30 to -45vh (up)
  const dx = (Math.random() - 0.5) * 30;              // ±15vw sideways
  const rotate = (Math.random() - 0.5) * 70;          // ±35°
  return { dx, dy, rotate };
}

function useSelectionSequence(selection) {
  return useMemo(() => {
    if (!selection || !selection.words) return [];
    if (selection.mode === "hebrew") {
      return buildHebrewSequence(selection.words, { root: "C", modeIdx: 0 });
    }
    return buildLetterSequence(selection.words);
  }, [selection]);
}

/**
 * Projected big-screen view. Listens to SSE for new selections, then
 * animates the letters one-by-one onto a full-viewport Staff. Old
 * selections cross-fade out. This is the audience-facing surface, so we
 * lean hard on drama: pure black, drifting letters, big cursive names.
 */
export default function LiveDisplay() {
  const { slug } = useParams();
  const { lastSelection: rawSelection, setLastSelection, mode, event, applyEvent, connected } = useLiveStream(slug);
  // Selections older than the event's `selection_cleared_at` stamp (set
  // when Dan lands on an app slide) are no longer on the staff.
  const clearedAtMs = event?.selection_cleared_at
    ? new Date(event.selection_cleared_at).getTime()
    : 0;
  const lastSelection =
    rawSelection && new Date(rawSelection.selected_at).getTime() > clearedAtMs ? rawSelection : null;
  const sequence = useSelectionSequence(lastSelection);
  // One authoritative event, owned by the stream hook: the initial fetch, the
  // live stream and the 4s reconcile poll all merge into it monotonically, so
  // a dropped frame can't leave the projector on the wrong slide.
  const effectiveEvent = event;
  const fantasiaGhostsOn = effectiveEvent?.fantasia_ghosts_on !== false;
  const fantasiaSmokeOn = !!effectiveEvent?.fantasia_smoke_on;
  const [dropped, setDropped] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Fantasia-style ghost letters that float on top of the page in Hebrew
  // mode. Each ghost has its own drift/scale/color and self-purges after
  // the animation completes.
  const [ghosts, setGhosts] = useState([]);
  const timerRef = useRef(null);
  const lastRenderedRef = useRef(null);
  const staffCardRef = useRef(null);
  const smokeRef = useRef(null);
  const smokeCursorRef = useRef(0);

  /**
   * Splat the smoke canvas for the current note. Two design decisions:
   *
   * 1. HORIZONTAL: splats are distributed evenly across the FULL viewport
   *    width by sequence index (not by the note's staff-pixel position),
   *    so the smoke covers the whole projector, not just the ~52% band
   *    that the staff notes physically occupy.
   *
   * 2. VERTICAL: splat AT the note's y-position, then let a downward
   *    velocity impulse carry the dye toward the bottom of the screen —
   *    this is what makes it read as SMOKE (motion + dissipation), not
   *    a static blob. `dy` is negative because the fluid sim's velocity
   *    Y-axis points up in DOM terms.
   */
  const splatFromNote = (noteIdx, totalNotes) => {
    if (!smokeRef.current) return;
    const el = document.querySelector(`[data-testid="staff-note-${noteIdx}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const jitterX = (Math.random() - 0.5) * (window.innerWidth * 0.04);
    const evenX = ((noteIdx + 0.5) / Math.max(1, totalNotes)) * window.innerWidth;
    const x = evenX + jitterX;
    const y = r.top + r.height * 0.7;
    // Colours advance through the palette continuously across the whole
    // show (rather than restarting at index 0 for every word), so two
    // four-letter words never come up in the same four colours.
    const color = SMOKE_PALETTE[smokeCursorRef.current % SMOKE_PALETTE.length];
    smokeCursorRef.current += 1;
    const dx = 70;
    const dy = -(380 + Math.random() * 80);
    smokeRef.current.splatAt({ x, y, color, dx, dy });
    // Tiny observability hook: WebGL splats can't be inspected from the
    // DOM, so keep the last colours around for QA.
    window.__splatColors = [...(window.__splatColors || []), color].slice(-32);
  };

  const spawnGhost = (letter, dur) => {
    const el = staffCardRef.current;
    if (!el) return;
    // Ghosts erupt from the TOP EDGE of the staff card and float
    // upward toward the headline, fading as they rise. This pairs
    // visually with the colored smoke pooling BELOW the staff — the
    // two effects sandwich the notation between rising ghosts and
    // falling smoke.
    const rect = el.getBoundingClientRect();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { cx, cy } = pickStaffTopSpawn(rect, window.innerWidth);
    const { dx, dy, rotate } = pickDriftUp();
    const box = Math.max(180, rect.width * 0.14);
    const color = GHOST_PALETTE[Math.floor(Math.random() * GHOST_PALETTE.length)];
    const seed = Math.floor(Math.random() * 999);
    setGhosts((prev) => [
      ...prev,
      { id, letter, cx, cy, box, dx, dy, rotate, color, seed, dur },
    ]);
    setTimeout(() => {
      setGhosts((prev) => prev.filter((g) => g.id !== id));
    }, dur + 200);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ev, cur] = await Promise.all([
          axios.get(`${API}/live/${slug}`),
          axios.get(`${API}/live/${slug}/selections/current`),
        ]);
        if (cancelled) return;
        applyEvent(ev.data);
        if (cur.data.selection) setLastSelection(cur.data.selection);
      } catch (err) {
        logError("[display] initial event/selection fetch failed", err);
        /* SSE reconnect or the empty state takes over from here */
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Animate a new selection by dropping its letters onto the staff over time.
  useEffect(() => {
    if (!sequence.length) return undefined;
    if (lastRenderedRef.current === lastSelection?.id) return undefined;
    lastRenderedRef.current = lastSelection?.id;

    if (timerRef.current) clearInterval(timerRef.current);
    setDropped([]);
    setActiveIndex(-1);
    setGhosts([]);
    let i = 0;
    const collected = [];
    const isHebrewSel = lastSelection?.mode === "hebrew";
    const ghostDur = computeGhostDuration(sequence.length);
    // Small delay before the first drop so the audience notices the change.
    const startAt = setTimeout(() => {
      timerRef.current = setInterval(() => {
        if (i >= sequence.length) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // Leave the last note glowing for ~1s, then dim it.
          setTimeout(() => setActiveIndex(-1), 900);
          return;
        }
        const entry = sequence[i];
        collected.push(entry);
        setDropped([...collected]);
        setActiveIndex(i);
        // Fantasia ghost letters (Hebrew mode only, gated by the
        // per-event toggle from the moderator console).
        if (isHebrewSel && entry.letter && fantasiaGhostsOn) {
          spawnGhost(entry.letter, ghostDur);
        }
        // Fantasia smoke splat at the note's pixel position. Gated by
        // the per-event toggle. We wait ~500ms so the spring drop-in
        // has settled and the splat lands on the notehead, not above.
        if (fantasiaSmokeOn) {
          const noteIdxAtDrop = i;
          const totalAtDrop = sequence.length;
          setTimeout(() => splatFromNote(noteIdxAtDrop, totalAtDrop), 500);
        }
        i += 1;
      }, STEP_MS);
    }, 250);

    return () => {
      clearTimeout(startAt);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sequence, lastSelection?.id]);

  const currentWords = lastSelection?.words?.join(" ") || "";
  const currentAuthor = lastSelection
    ? lastSelection.source === "admin"
      ? "Dan"
      : lastSelection.participant_name || "the room"
    : "";
  const isHebrew = (lastSelection?.mode || mode) === "hebrew";

  // Resolve the current slide from the effective event. If the event
  // hasn't hydrated yet (initial GET pending) we fall back to nothing
  // and render the classic Fantasia experience; that keeps this page
  // backwards-compatible for existing events that predate the slide
  // feature (they'll be auto-backfilled by the backend on first admin
  // interaction, and until then they render as before).
  const eventSlides = effectiveEvent?.slides || [];
  const currentSlideKey = effectiveEvent?.current_slide_key;
  const currentSlide = eventSlides.find((s) => s.key === currentSlideKey);
  // "app" slides are Act 2 Scene 3's second sub-slide + Act 3 Scene 2 —
  // the classic Fantasia experience takes over. Every other slide type
  // renders through <SlideRenderer/> on top of the smoke canvas.
  const isAppSlide = currentSlide?.type === "app";
  const showAppExperience = !currentSlide || isAppSlide;
  // When a non-app slide is showing, the SlideRenderer paints an opaque
  // #060312 background on top of the entire viewport. Any Fantasia
  // visuals (drifting letters, floating notes, smoke canvas) rendered
  // behind it would be occluded but still animate — that doubles the
  // active animation count (SlideShell mounts its OWN Drifting/Floating
  // instances) and starves the compositor, which is what caused the
  // floating notes on slides to look jerky. So we hide the LiveDisplay-
  // level background layers whenever a slide overlay is up.
  const showFantasiaLayers = showAppExperience;

  // Arriving on an app slide (Act 2 Scene 3 / Act 3 Scene 2) starts with
  // a clean staff — whatever was last pushed during an earlier scene is
  // wiped so Dan opens on an empty stage. First hydrate is skipped so a
  // display joining mid-scene keeps the live selection.
  const prevSlideKeyRef = useRef(null);
  useEffect(() => {
    if (!currentSlideKey) return;
    const prev = prevSlideKeyRef.current;
    if (prev === currentSlideKey) return;
    prevSlideKeyRef.current = currentSlideKey;
    if (prev === null) return;
    if (currentSlide?.type !== "app") return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    lastRenderedRef.current = null;
    setDropped([]);
    setActiveIndex(-1);
    setGhosts([]);
    setLastSelection(null);
  }, [currentSlideKey, currentSlide?.type, setLastSelection]);

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ backgroundColor: "#060312", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }}
      data-live-dark=""
      data-testid="live-display-page"
    >
      <NoIndex title="Compose Yourself! by Dan Berry" />
      {/* Shared CSS keyframes for the ghost fade curve. Framer-motion
          doesn't reliably animate multi-stop keyframed opacity in this
          version so we drive fade via plain CSS while framer handles
          the transforms + SVG filter animation drives the dissolve. */}
      <style>{`
        @keyframes gf-fade {
          0%   { opacity: 0; }
          10%  { opacity: 0.95; }
          100% { opacity: 0; }
        }
      `}</style>

      {/* Live-feed health. Sits above the slide overlay so Dan can see it
          from the stage if the venue wifi hiccups. */}
      {!connected ? (
        <div
          className="fixed top-5 right-6 z-[70] text-xs"
          style={{
            color: "rgba(245,239,227,0.55)",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontFamily: "'Courier Prime', monospace",
          }}
          data-testid="live-display-reconnecting"
        >
          reconnecting
        </div>
      ) : null}

      {/* Presentation slide overlay. When the event's `current_slide_key`
          points to a non-app slide, we render the SlideRenderer full-
          screen ON TOP of the Fantasia canvas. On app-embedded slides
          (Act 2 Scene 3, Act 3 Scene 2) we skip this and let the
          existing staff + ghosts + smoke experience show through — but
          we DO overlay the 9-dot progress tracker at the top so the
          audience still knows where they are in the show. */}
      {currentSlide && !showAppExperience ? (
        <div
          className="absolute inset-0 z-40"
          style={{ backgroundColor: "#060312" }}
          data-testid="live-display-slide"
        >
          <SlideRenderer
            slide={currentSlide}
            slides={eventSlides}
            currentSlideKey={currentSlideKey}
            audienceUrl={`${window.location.origin}/live/${slug}`}
            slug={slug}
            recapStartedAt={effectiveEvent?.recap_playback_started_at || null}
            recapStepMs={effectiveEvent?.recap_playback_step_ms || 15000}
            volunteerNotes={effectiveEvent?.volunteer_notes || []}
            hebrewDemo={effectiveEvent?.hebrew_demo || null}
          />
        </div>
      ) : null}

      {/* App-slide progress dots — visible ONLY when an "app" slide is
          the current slide, so Dan can see the show progress even while
          the interactive Fantasia experience is running. */}
      {isAppSlide ? (
        <div
          className="absolute top-0 inset-x-0 z-40 flex justify-center pt-6 pointer-events-none"
          data-testid="live-display-app-dots"
        >
          <ProgressDots slides={eventSlides} currentSlideKey={currentSlideKey} />
        </div>
      ) : null}

      <div className="absolute inset-0 pointer-events-none">
        {showFantasiaLayers && <DriftingLetters count={isHebrew ? 22 : 40} />}
        {showFantasiaLayers && <FloatingNotes count={9} />}
      </div>

      {/* Fantasia smoke — a WebGL fluid sim that gets a colored splat
          at each note's pixel position as it lands. Sits behind the
          ghost letters and headline via z-[0], transparent so the
          deep-navy background still reads. Only mounted when the
          per-event toggle is on (otherwise idle GPU cost). */}
      {fantasiaSmokeOn && showFantasiaLayers && <SmokeCanvas ref={smokeRef} />}

      {/* Fantasia ghost letters (Hebrew mode only). Each letter that drops
          onto the staff also throws a giant painterly copy onto the page
          that drifts, grows, and dissolves like ink dispersing in water.

          Each ghost is its own inline <svg>: HTML+CSS filter chains on
          text glyphs render inconsistently across browsers at high
          displacement values, but an SVG-native `<text>` + `<filter>`
          combo produces the expected particle scattering everywhere.
          `feTurbulence` generates fractal noise whose frequency ramps up
          over the ghost's lifetime; `feDisplacementMap` pushes the
          letter's pixels along that noise field with an increasing
          displacement scale. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]" data-testid="live-display-ghosts">
        <AnimatePresence>
          {ghosts.map((g) => (
            <GhostLetter key={g.id} ghost={g} />
          ))}
        </AnimatePresence>
      </div>

      {/* Event badge, top-left */}
      <div className="absolute top-6 left-8 z-10 opacity-70">
        <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>Live</p>
        <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>{event?.name || ""}</p>
      </div>

      {/* Current word display (huge) */}
      <div className="relative pt-16 pb-4 text-center z-[1]">
        <AnimatePresence mode="wait">
          {currentWords ? (
            <motion.div
              key={lastSelection?.id || "empty"}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
              className="px-8"
              data-testid="live-display-words"
              dir={isHebrew ? "rtl" : "ltr"}
            >
              <p
                className="leading-[0.95] mx-auto"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: "clamp(60px, 11vw, 200px)",
                  color: "#F5EFE3",
                  textShadow: "0 0 60px rgba(232,196,138,0.35)",
                  textWrap: "balance",
                }}
              >
                {currentWords}
              </p>
              <p className="mt-6 text-sm sm:text-base uppercase tracking-[0.32em]" style={{ color: "#F2D589" }} dir="ltr">
                — {currentAuthor}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="pt-16"
              data-testid="live-display-idle"
            >
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 3.6vw, 56px)", fontStyle: "italic", color: "rgba(245,239,227,0.5)" }}>
                Compose yourself&hellip;
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Staff, projected across the bottom half. In Live mode we go
          edge-to-edge: no maxWidth cap, height auto-scales via the SVG
          viewBox, so the staff fills whatever projector or screen it
          lands on. A `ref` here lets us anchor Fantasia ghosts to the
          actual card position (Hebrew renders in 1 or 2 lines above,
          so vh coordinates would drift). */}
      {showFantasiaLayers ? (
      <div className="relative px-6 sm:px-12 pb-10 z-[1]" style={{ marginTop: "3rem" }}>
        <div ref={staffCardRef}>
          <Staff droppedEntries={dropped} activeIndex={activeIndex} fluid reserveEntries={sequence} />
        </div>
      </div>
      ) : null}
    </div>
  );
}


